/**
 * Documents Tab - Lists, uploads, downloads and deletes documents for a customer.
 * Supports importance filter, category filter, list/tiles view, drag-drop upload.
 */

import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Loader2, FileText, Upload, Download, Trash2, Search, File,
  FileSpreadsheet, Image, FileArchive, Link, LayoutGrid, List, Filter,
} from 'lucide-react';
import {
  documentsAPI, type CustomerProfile, type Document as DocType, type DocumentCategory,
} from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { toast } from 'sonner';
import { DocumentDetailModal } from './DocumentDetailModal';

interface DocumentsTabProps {
  customer: CustomerProfile;
}

type ViewMode = 'list' | 'tiles';
type ImportanceFilter = 'all' | 'low' | 'medium' | 'high' | 'critical';

const IMPORTANCE_CONFIG: Record<string, { label: string; className: string }> = {
  low: { label: 'Low', className: 'bg-gray-100 text-gray-700' },
  medium: { label: 'Medium', className: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', className: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Critical', className: 'bg-red-100 text-red-700' },
};

function getFileIcon(doc: DocType) {
  if (doc.document_type === 'url') return <Link className="h-5 w-5 text-blue-500" />;
  const ext = (doc.file_extension || '').toLowerCase();
  if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return <FileText className="h-5 w-5 text-red-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp'].includes(ext)) return <Image className="h-5 w-5 text-purple-500" />;
  if (['zip', 'rar', '7z'].includes(ext)) return <FileArchive className="h-5 w-5 text-amber-600" />;
  return <File className="h-5 w-5 text-gray-500" />;
}

function ImportanceBadge({ importance }: { importance?: string }) {
  if (!importance) return null;
  const config = IMPORTANCE_CONFIG[importance];
  if (!config) return null;
  return <Badge className={`text-[10px] px-1.5 py-0 ${config.className}`}>{config.label}</Badge>;
}

export function DocumentsTab({ customer }: DocumentsTabProps) {
  const { formatDate } = useFormatters();
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<DocType[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [detailDoc, setDetailDoc] = useState<DocType | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [importanceFilter, setImportanceFilter] = useState<ImportanceFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.company]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await documentsAPI.getDocuments({ company: customer.company, ordering: '-uploaded_at' });
      setDocuments(res.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await documentsAPI.getCategories();
      const data = res.data as unknown;
      setCategories(Array.isArray(data) ? data : ((data as { results?: DocumentCategory[] }).results || []));
    } catch (err: unknown) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        formData.append('company', String(customer.company));
        formData.append('document_type', 'file');
        formData.append('is_public', 'false');
        await documentsAPI.createDocument(formData);
        successCount++;
      } catch (err: unknown) {
        console.error(`Failed to upload ${file.name}:`, err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded`);
      await fetchDocuments();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (doc: DocType) => {
    const ok = await confirm({
      title: 'Delete Document',
      description: `Are you sure you want to delete "${doc.title}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      setDeletingId(doc.id);
      await documentsAPI.deleteDocument(doc.id);
      toast.success('Document deleted');
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err: unknown) {
      console.error('Failed to delete document:', err);
      toast.error('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (doc: DocType) => {
    try {
      const res = await documentsAPI.downloadDocument(doc.id);
      if (res.data.file_url) window.open(res.data.file_url, '_blank');
    } catch (err: unknown) {
      console.error('Failed to download document:', err);
      toast.error('Failed to download document');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  // Apply filters
  const filtered = documents.filter((d) => {
    if (search) {
      const q = search.toLowerCase();
      if (!d.title.toLowerCase().includes(q) &&
          !(d.description || '').toLowerCase().includes(q) &&
          !(d.tags || '').toLowerCase().includes(q)) return false;
    }
    if (importanceFilter !== 'all' && d.importance !== importanceFilter) return false;
    if (categoryFilter !== 'all') {
      const catId = parseInt(categoryFilter, 10);
      if (d.category !== catId) return false;
    }
    return true;
  });

  const hasActiveFilters = importanceFilter !== 'all' || categoryFilter !== 'all';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documents
            {!loading && <Badge variant="outline" className="ml-2">{documents.length}</Badge>}
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-40" />
            </div>
            <Button variant={showFilters ? 'secondary' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-1" />
              Filters{hasActiveFilters ? ' *' : ''}
            </Button>
            <div className="flex border rounded-md">
              <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-r-none" onClick={() => setViewMode('list')} title="List"><List className="h-4 w-4" /></Button>
              <Button variant={viewMode === 'tiles' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-l-none" onClick={() => setViewMode('tiles')} title="Tiles"><LayoutGrid className="h-4 w-4" /></Button>
            </div>
            <Button size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Upload
            </Button>
            <input ref={fileInputRef} type="file" multiple accept=".png,.jpg,.jpeg,.bmp,.doc,.docx,.xls,.xlsx,.pdf,.ppt,.pptx,.txt,.svg,.csv,.zip,.rar,.7z" className="hidden" onChange={(e) => handleUpload(e.target.files)} />
          </div>
        </div>
        {/* Filter row */}
        {showFilters && (
          <div className="flex gap-3 mt-3 flex-wrap">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Importance</label>
              <select value={importanceFilter} onChange={(e) => setImportanceFilter(e.target.value as ImportanceFilter)} className="text-sm border rounded-md px-2 py-1.5 bg-background">
                <option value="all">All</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="text-sm border rounded-md px-2 py-1.5 bg-background">
                <option value="all">All</option>
                {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>
            {hasActiveFilters && (
              <div className="flex items-end">
                <Button variant="ghost" size="sm" onClick={() => { setImportanceFilter('all'); setCategoryFilter('all'); }}>Clear</Button>
              </div>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className={`border-2 border-dashed rounded-lg p-6 mb-4 text-center transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}>
          <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Drag files here, or <button className="text-primary underline" onClick={() => fileInputRef.current?.click()}>click to upload</button></p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : error ? (
          <div className="text-center py-12 text-red-600"><p>{error}</p>
            <button onClick={fetchDocuments} className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{search || hasActiveFilters ? 'No documents match your filters.' : 'No documents uploaded for this customer yet.'}</p>
          </div>
        ) : viewMode === 'list' ? (
          <DocListView docs={filtered} formatDate={formatDate} deletingId={deletingId} onDownload={handleDownload} onDelete={handleDelete} onOpenDetail={setDetailDoc} />
        ) : (
          <DocTilesView docs={filtered} formatDate={formatDate} deletingId={deletingId} onDownload={handleDownload} onDelete={handleDelete} onOpenDetail={setDetailDoc} />
        )}
      </CardContent>

      {/* Document Detail Modal */}
      {detailDoc && (
        <DocumentDetailModal
          document={detailDoc}
          categories={categories}
          onClose={() => setDetailDoc(null)}
          onUpdated={(updated) => {
            setDocuments((prev) => prev.map((d) => d.id === updated.id ? updated : d));
            setDetailDoc(updated);
          }}
        />
      )}
    </Card>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

interface DocViewProps {
  docs: DocType[];
  formatDate: (d: string) => string;
  deletingId: number | null;
  onDownload: (doc: DocType) => void;
  onDelete: (doc: DocType) => void;
  onOpenDetail: (doc: DocType) => void;
}

function DocListView({ docs, formatDate, deletingId, onDownload, onDelete, onOpenDetail }: DocViewProps) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Name</th>
            <th className="text-left px-4 py-2 font-medium hidden md:table-cell">Importance</th>
            <th className="text-left px-4 py-2 font-medium hidden md:table-cell">Size</th>
            <th className="text-left px-4 py-2 font-medium hidden lg:table-cell">Uploaded</th>
            <th className="text-left px-4 py-2 font-medium hidden lg:table-cell">By</th>
            <th className="text-right px-4 py-2 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((doc) => (
            <tr key={doc.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => onOpenDetail(doc)}>
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  {getFileIcon(doc)}
                  <span className="truncate max-w-[250px]">{doc.title}</span>
                  {doc.category_name && <Badge variant="outline" className="text-xs">{doc.category_name}</Badge>}
                </div>
              </td>
              <td className="px-4 py-2 hidden md:table-cell"><ImportanceBadge importance={doc.importance} /></td>
              <td className="px-4 py-2 text-muted-foreground hidden md:table-cell">{doc.formatted_file_size || '—'}</td>
              <td className="px-4 py-2 text-muted-foreground hidden lg:table-cell">{formatDate(doc.uploaded_at)}</td>
              <td className="px-4 py-2 text-muted-foreground hidden lg:table-cell truncate max-w-[150px]">{doc.uploaded_by_name || '—'}</td>
              <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDownload(doc)} title="Download"><Download className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => onDelete(doc)} disabled={deletingId === doc.id} title="Delete">
                    {deletingId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocTilesView({ docs, formatDate, deletingId, onDownload, onDelete, onOpenDetail }: DocViewProps) {
  return (
    <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      {docs.map((doc) => (
        <Card key={doc.id} className="group relative hover:shadow-md transition-shadow cursor-pointer" onClick={() => onOpenDetail(doc)}>
          <CardContent className="pt-4 flex flex-col items-center text-center gap-2">
            <div className="w-16 h-16 flex items-center justify-center">{getFileIcon(doc)}</div>
            <p className="text-xs font-medium truncate w-full" title={doc.title}>{doc.title}</p>
            <ImportanceBadge importance={doc.importance} />
            <p className="text-[10px] text-muted-foreground">{doc.formatted_file_size || ''}</p>
            <p className="text-[10px] text-muted-foreground">{formatDate(doc.uploaded_at)}</p>
            <div className="flex gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDownload(doc)} title="Download"><Download className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => onDelete(doc)} disabled={deletingId === doc.id} title="Delete">
                {deletingId === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
